import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CapQuestLogo, EyeIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { GoogleAuthButton } from '../../components/google-auth-button/index';
import { Input } from '../../components/input/index';
import OrDelimiter from '../../components/or-delimiter/index';
import { BackendRoute } from '../../config/index';
import { HttpException } from '../../exceptions/index';
import { useAppMutation } from '../../hooks/index';
import { ApiService } from '../../services/index';
import { accessTokenStorage } from '../../storage/auth/index';
import { inviteStakeholderStore } from '../../storage/local/index';
import {
  clearPostAuthRedirect,
  getSafeRedirectFromSearch,
  navigateToInternalRedirect,
  persistPostAuthRedirect,
} from '../../utils/auth-redirect.util';

interface LoginDto {
  email: string;
  password: string;
}

const Login = () => {
  const [isPasswordShown, setPasswordShown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailParam = queryParams.get('email');
  const redirectTo = getSafeRedirectFromSearch(location.search) ?? '/';
  const [loginDto, setLoginDto] = useState<LoginDto>({
    email: emailParam || '',
    password: '',
  });

  const googleAuthUrl =
    redirectTo !== '/'
      ? `${process.env.REACT_APP_BACKEND_URL}/auth/google?redirect=${encodeURIComponent(redirectTo)}`
      : `${process.env.REACT_APP_BACKEND_URL}/auth/google`;

  const handleGoogleAuthClick = useCallback(() => {
    persistPostAuthRedirect(redirectTo);
  }, [redirectTo]);

  const loginMutation = useAppMutation<void, HttpException>([], {
    mutationFn: async () => {
      await ApiService.clearStaleAuthSession();

      const { accessToken } = await ApiService.post<{ accessToken: string }>(
        { endpoint: BackendRoute.AUTH_LOGIN },
        { body: loginDto },
      );
      accessTokenStorage.getState().set(accessToken);
    },
    onSuccess: () => {
      clearPostAuthRedirect();
      navigateToInternalRedirect(navigate, redirectTo);
    },
    defaultErrorHandling: false,
  });

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!loginDto.email.trim() || !loginDto.password.trim()) return;
      loginMutation.mutate();
    },
    [loginDto.email, loginDto.password, loginMutation],
  );

  const handleInvitationSync = useCallback(() => {
    const invitation = inviteStakeholderStore.get();
    if (!invitation) return;

    if (invitation.isRegistered) {
      inviteStakeholderStore.set({ ...invitation, isRegistered: false });
      navigate('/login');
      return;
    }

    setLoginDto((prev) => ({ ...prev, email: invitation.email }));
  }, [navigate]);

  const loginErrorMessage = loginMutation.error?.body?.message;
  const normalizedLoginErrorMessage = Array.isArray(loginErrorMessage)
    ? (loginErrorMessage[0] ?? '')
    : (loginErrorMessage ?? '');
  const formattedLoginErrorMessage = normalizedLoginErrorMessage
    ? normalizedLoginErrorMessage.charAt(0).toUpperCase() + normalizedLoginErrorMessage.slice(1)
    : '';

  useEffect(() => {
    const redirect = getSafeRedirectFromSearch(location.search);
    if (redirect) {
      persistPostAuthRedirect(redirect);
    }
  }, [location.search]);

  useEffect(() => {
    void ApiService.clearStaleAuthSession();
  }, []);

  useEffect(() => {
    handleInvitationSync();
  }, [handleInvitationSync]);

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-gray-50 font-inter">
      <div className="max-h-full max-lg:h-full max-lg:w-full">
        <div
          className="flex h-fit min-h-full w-full flex-col gap-8 rounded-lg bg-white px-4 pb-8 pt-16 lg:h-fit lg:w-[458px] lg:items-center lg:px-8 lg:pt-8 lg:text-center"
          style={{
            filter:
              'drop-shadow(0px 1px 2px #1018280F) drop-shadow(0px 2px 3px #1018281A) drop-shadow(0px -1px 3px #1018280A)',
          }}
        >
          <CapQuestLogo size={48} />
          <div className="flex flex-col gap-2">
            <div
              className="!bg-clip-text text-4xl-mobile font-bold text-transparent lg:text-4xl"
              style={{
                background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
              }}
            >
              <AppFormattedMessage id="WELCOME_BACK" />
            </div>
            <div className="text-sm text-gray-500">
              <AppFormattedMessage id="SIGN_UP_TO_CAPQUEST" />
            </div>
          </div>
          <div className="flex w-full flex-col gap-6">
            <a className="block no-underline" href={googleAuthUrl} onClick={handleGoogleAuthClick}>
              <GoogleAuthButton />
            </a>

            <OrDelimiter />

            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <Input
                isError={loginMutation.isError}
                onChange={(value) => setLoginDto({ ...loginDto, email: value })}
                placeholder={<AppFormattedMessage id="EMAIL_ADDRESS" />}
                type="email"
                value={loginDto.email}
              />
              <Input
                errorMessage={formattedLoginErrorMessage}
                icon={<EyeIcon variant={isPasswordShown ? 'ACTIVE' : 'CLOSE'} />}
                isError={loginMutation.isError}
                onChange={(value) => setLoginDto({ ...loginDto, password: value })}
                onIconClick={() => setPasswordShown((prev) => !prev)}
                placeholder={<AppFormattedMessage id="PASSWORD" />}
                type={isPasswordShown ? 'text' : 'password'}
                value={loginDto.password}
              />
              <div className="flex justify-end">
                <button
                  className="text-sm font-[450] text-brand-700 underline"
                  onClick={() => navigate('/forgot-password')}
                  type="button"
                >
                  <AppFormattedMessage id="FORGOT_PASSWORD" />?
                </button>
              </div>
              <div className="mt-2 w-full">
                <Button
                  disabled={!loginDto.email.trim() || !loginDto.password.trim()}
                  isLoading={loginMutation.isPending}
                  type="submit"
                >
                  <AppFormattedMessage id="LOG_IN" />
                </Button>
              </div>
            </form>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <div className="font-[450] text-gray-700">
              <AppFormattedMessage id="DONT_HAVE_AN_ACCOUNT_YET" />?
            </div>
            <button
              className="font-[550] text-brand-700 underline"
              onClick={() => navigate('/register')}
            >
              <AppFormattedMessage id="SIGN_UP" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
